



















































































































































































import * as d3 from 'd3';
import Vue from 'vue';
import { query, mutation } from '@/dex-shared/helpers/graphql';
import pxButton from '@/dex-shared/components/px-button.vue';

const NODE_PROPS = `
  id
  name
  nodePath
  fullName
  description
  parentId
  siblingOrder
  ltreeKnowledgesByParentId{
    nodes{
      id
    }
  }`;

export default {
  components: {
    pxButton,
  },
  data() {
    return {
      categoryOptions: {},
      dataset: {},
      graphPadding: 50,
      hasExpandedChildren: {},
      heightLevelFactor: 100,
      newParentId: null,
      nodeName: '',
      nodeDescription: '',
      nodeHeight: 50,
      nodeWidthFactor: 6,
      root: null,
      rootOptions: {},
      selectedNodes: {},
      selectedCategoryId: '',
      selectedRootId: '',
      showManual: false,
      widthLevelFactor: 200,
    };
  },
  computed: {
    sortedCategoryOptions() {
      return this.sortOptionsByName(this.categoryOptions);
    },
    sortedRootOptions() {
      return this.sortOptionsByName(this.rootOptions);
    },
    descendants() {
      if (this.root) {
        return this.root.descendants();
      }
      return undefined;
    },
    getTreeLayout() {
      return d3.tree().size(this.size);
    },
    linkPaths() {
      if (this.links) {
        return this.links.map(
          d3
            .linkVertical()
            .x((item: any) => item.x)
            .y((item: any) => item.y),
        );
      }
      return undefined;
    },
    links() {
      if (this.root) {
        return this.root.links();
      }
      return undefined;
    },
    size() {
      return [this.width, this.height];
    },
    showAddButton() {
      return Object.keys(this.selectedNodes).length === 1;
    },
    showEditButton() {
      return Object.keys(this.selectedNodes).length === 1;
    },
    showExcludeButton() {
      const keys = Object.keys(this.selectedNodes);
      if (keys.length !== 1) {
        return false;
      }
      const id = keys[0];
      if (this.dataset[id].ltreeKnowledgesByParentId.nodes.length !== 0) {
        return false;
      }
      return true;
    },
    showInvertButton() {
      const keys = Object.keys(this.selectedNodes);
      if (keys.length !== 2) {
        return false;
      }
      if (this.dataset[keys[0]].parentId !== this.dataset[keys[1]].parentId) {
        return false;
      }
      return true;
    },
    showNewParentButton() {
      const keys = Object.keys(this.selectedNodes);
      if (keys.length === 1 && this.newParentId) {
        const childId = keys[0];
        if (+childId === +this.newParentId) {
          return false;
        }
        const parentPath = this.dataset[this.newParentId].nodePath;
        if (parentPath.split('.').indexOf(childId) < 0) {
          return true;
        }
      }
      return false;
    },
    stratify() {
      return d3
        .stratify()
        .id((d: any) => d.id)
        .parentId((d: any) => d.parentId);
    },
    treeLevelsCount() {
      if (this.root) {
        return this.root.height + 1;
      }
      return 0;
    },
    maxTreeNodesInALevel() {
      const levelCount = {};
      Object.values(this.dataset).forEach((node: any) => {
        const level = node.nodePath.split('.').length;
        if (levelCount.hasOwnProperty(level)) {
          levelCount[level] += 1;
        } else {
          levelCount[level] = 1;
        }
      });
      const counts: any = Object.values(levelCount);
      if (counts.length > 0) {
        return Math.max(...counts);
      }
      return 0;
    },
    width() {
      return this.maxTreeNodesInALevel * this.widthLevelFactor;
    },
    height() {
      return this.heightLevelFactor * this.treeLevelsCount;
    },
  },
  watch: {
    dataset: {
      handler: function(val) {
        if (val) {
          this.root = { ...val };
          // category knowledge must not be part of the visualization
          this.root[this.selectedRootId].parentId = null;
          this.root = Object.values(this.root);
          this.root.sort((left, right) => {
            if (left.siblingOrder < right.siblingOrder) {
              return -1;
            }
            if (left.siblingOrder > right.siblingOrder) {
              return 1;
            }
            return 0;
          });
          this.root = this.stratify(this.root);
          this.root = this.getTreeLayout(this.root);
        }
      },
      deep: true,
    },
    selectedCategoryId: {
      handler: function(val) {
        this.rootOptions = {};
        if (val) {
          this.getChildrenNodes(val).then(rootOptions => {
            rootOptions.forEach(rootOption => {
              Vue.set(this.rootOptions, rootOption.id, rootOption);
            });
          });
        }
        this.root = null;
        this.selectedRootId = '';
      },
    },
    selectedRootId: {
      handler: function(val) {
        if (val) {
          this.dataset = {
            [val]: this.rootOptions[val],
          };
        }
        this.selectedNodes = {};
        this.hasExpandedChildren = {};
        this.newParentId = null;
        this.nodeName = '';
        this.nodeDescription = '';
      },
    },
  },
  created() {
    this.getRootNodes().then(categoryOptions => {
      categoryOptions.forEach(categoryOption => {
        Vue.set(this.categoryOptions, categoryOption.id, categoryOption);
      });
    });
  },
  methods: {
    sortOptionsByName(options: Object) {
      return Object.values(options).sort((left, right) => {
        const lName = left.name.toLowerCase();
        const rName = right.name.toLowerCase();
        if (lName < rName) {
          return -1;
        }
        if (lName > rName) {
          return 1;
        }
        return 0;
      });
    },
    getNodeColor(id: number) {
      if (this.newParentId === +id) {
        return 'red';
      }
      return this.selectedNodes[id] ? 'blue' : 'grey';
    },
    alertInvalidName() {
      if (!this.nodeName.trim()) {
        alert('Nome inválido, preencha o nome e tente novamente.');
      } else {
        alert(
          'Nome selecionado inválido, já existe um conhecimento com esse nome.',
        );
      }
    },
    altClickOnNode(id: number) {
      if (this.selectedNodes[id]) {
        return;
      }
      if (this.newParentId === id) {
        this.newParentId = null;
      } else {
        this.newParentId = id;
      }
    },
    clickOnNode(id: number) {
      if (id === this.newParentId) {
        return;
      }
      if (this.selectedNodes[id]) {
        Vue.delete(this.selectedNodes, id);
        return;
      }
      if (Object.keys(this.selectedNodes).length < 2) {
        Vue.set(this.selectedNodes, id, true);
      }
    },
    clickOnExcludeButton() {
      const id = +Object.keys(this.selectedNodes)[0];
      const parentId = this.dataset[id].parentId;
      this.deleteNode(id)
        .then(() => {
          if (parentId) {
            this.removeChildFromDatasetItem(id, parentId);
            Vue.delete(this.selectedNodes, id);
            Vue.delete(this.dataset, id);
          } else {
            Vue.delete(this.rootOptions, id);
            this.selectedRootId = Object.keys(this.rootOptions)[0];
          }
        })
        .catch(() => {
          alert(
            'O conhecimento selecionado está em uso em alguma associação e não pode ser removido',
          );
        });
    },
    clickOnAddButton() {
      const parentId = +Object.keys(this.selectedNodes)[0];
      this.createNode(
        parentId,
        this.nodeName.trim(),
        this.nodeDescription.trim(),
      )
        .then(newNodeData => {
          // It's necessary to query again since the properties generated
          // by triggers are not returned by the graphql mutation.
          this.getNode(newNodeData.id).then(nodeDataWithAllProperties => {
            Vue.set(this.dataset, newNodeData.id, nodeDataWithAllProperties);
          });

          this.addChildToDatasetItem(newNodeData.id, parentId);
          this.nodeName = '';
          this.nodeDescription = '';
        })
        .catch(() => {
          this.alertInvalidName();
        });
    },
    clickOnEditButton() {
      const id = +Object.keys(this.selectedNodes)[0];
      this.editNode(id, this.nodeName.trim(), this.nodeDescription.trim())
        .then(editedNodeData => {
          Vue.set(this.dataset, editedNodeData.id, editedNodeData);
          this.nodeName = '';
          this.nodeDescription = '';
        })
        .catch(() => {
          this.alertInvalidName();
        });
    },
    clickOnNewParentButton() {
      const childId = +Object.keys(this.selectedNodes)[0];
      const oldParentId = this.dataset[childId].parentId;
      this.changeParent(childId, this.newParentId)
        .then(editedNodeData => {
          editedNodeData.nodePath = `${this.dataset[this.newParentId].nodePath}.${childId}`;
          Vue.set(this.dataset, childId, editedNodeData);
          this.addChildToDatasetItem(editedNodeData.id, this.newParentId);
          this.removeChildFromDatasetItem(childId, oldParentId);

          // removing children nodes since they have
          // outdated properties (nodePath and fullName)
          this.removeChildrenNodesFromDataset(editedNodeData.id);
          this.nodeName = '';
          this.nodeDescription = '';
        })
        .catch(() => {
          alert('Erro ao definir novo pai');
        });
    },
    clickOnInvertButton() {
      const keys = Object.keys(this.selectedNodes);
      const id1 = +keys[0];
      const id2 = +keys[1];
      this.swapSiblingOrder(id1, id2)
        .then(() => {
          const siblingOrder1 = this.dataset[id1].siblingOrder;
          const siblingOrder2 = this.dataset[id2].siblingOrder;
          Vue.set(this.dataset[id1], 'siblingOrder', siblingOrder2);
          Vue.set(this.dataset[id2], 'siblingOrder', siblingOrder1);
        })
        .catch(() => {
          alert('Erro ao alterar a ordem dos conhecimentos irmãos');
        });
    },
    clickOnAddRootButton() {
      this.createNode(
        this.selectedCategoryId,
        this.nodeName.trim(),
        this.nodeDescription.trim(),
      )
        .then(newNodeData => {
          // It's necessary to query again since the properties generated
          // by triggers are not returned by the graphql mutation.
          this.getNode(newNodeData.id).then(nodeDataWithAllProperties => {
            Vue.set(
              this.rootOptions,
              newNodeData.id,
              nodeDataWithAllProperties,
            );
          });
          this.nodeName = '';
          this.nodeDescription = '';
        })
        .catch(() => {
          this.alertInvalidName();
        });
    },
    addChildToDatasetItem(childId: number, parentId: number) {
      const siblings = this.dataset[parentId].ltreeKnowledgesByParentId.nodes;
      for (const sibling of siblings) {
        if (sibling.id === childId) {
          return;
        }
      }
      siblings.push({
        id: childId,
      });
      Vue.set(
        this.dataset[parentId].ltreeKnowledgesByParentId,
        'nodes',
        siblings,
      );
    },
    removeChildFromDatasetItem(childId: number, parentId: number) {
      const children = this.dataset[parentId].ltreeKnowledgesByParentId.nodes;
      let indexToRemove = null;
      for (const [index, child] of children.entries()) {
        if (child.id === childId) {
          indexToRemove = index;
          break;
        }
      }
      Vue.delete(
        this.dataset[parentId].ltreeKnowledgesByParentId.nodes,
        indexToRemove,
      );
      if (this.dataset[parentId].ltreeKnowledgesByParentId.nodes.length === 0) {
        Vue.delete(this.hasExpandedChildren, parentId);
      }
    },
    shiftClickOnNode(id: number) {
      if (!this.hasExpandedChildren[id]) {
        this.addChildrenNodesToDataset(id);
      } else {
        this.removeChildrenNodesFromDataset(id);
      }
      this.hasExpandedChildren[id] = !this.hasExpandedChildren[id];
    },
    getDisplayName(name: string) {
      if (name.length > 10) {
        return `${name.slice(0, 10)}.`;
      }
      return name;
    },
    removeChildrenNodesFromDataset(id: number) {
      this.dataset[id].ltreeKnowledgesByParentId.nodes.forEach(child => {
        if (this.hasExpandedChildren[child.id]) {
          this.removeChildrenNodesFromDataset(child.id);
          this.hasExpandedChildren[child.id] = false;
        }
        Vue.delete(this.selectedNodes, child.id);
        Vue.delete(this.dataset, child.id);
      });
    },
    addChildrenNodesToDataset(id: number) {
      const newDatasetItems = {};
      this.getChildrenNodes(id).then(results => {
        results.forEach(result => {
          newDatasetItems[result.id] = result;
        });
        this.dataset = {
          ...this.dataset,
          ...newDatasetItems,
        };
      });
    },
    async getChildrenNodes(id: number) {
      const results = await query(
        `query getChildrenNodes($id: Int!){
          ltreeKnowledgeById(id: $id){
            ltreeKnowledgesByParentId{
              nodes{
                ${NODE_PROPS}
              }
            }
          }
        }`,
        { id },
      );
      return results.data.ltreeKnowledgeById.ltreeKnowledgesByParentId.nodes;
    },
    async getNode(id: number) {
      const results = await query(
        `query getNode($id: Int!){
          ltreeKnowledgeById(id: $id){
            ${NODE_PROPS}
          }
        }`,
        { id },
      );
      return results.data.ltreeKnowledgeById;
    },
    async swapSiblingOrder(id1: number, id2: number) {
      await mutation(
        `mutation swapSiblingOrder($id1: Int!, $id2: Int!){
          swapSiblingOrder(
            input: {
              ltreeKnowledgeId1: $id1
              ltreeKnowledgeId2: $id2
            }
          ){
            clientMutationId
          }
        }`,
        {
          id1,
          id2,
        },
      );
    },
    async deleteNode(id: number) {
      await mutation(
        `mutation deleteNode($id: Int!){
          deleteLtreeKnowledgeById(input: {id: $id}){
            deletedLtreeKnowledgeId
          }
        }`,
        { id },
      );
    },
    async createNode(parentId: number, name: string, description: string) {
      description = description ? description : null;
      const results = await mutation(
        `mutation createNode($parentId: Int, $name: String!, $description: String){
          createLtreeKnowledge(
            input: {
              ltreeKnowledge: {
                parentId: $parentId,
                name: $name,
                description: $description
              }
            }
          ) {
            ltreeKnowledge {
              ${NODE_PROPS}
            }
          }
        }`,
        {
          parentId,
          name,
          description,
        },
      );
      return results.data.createLtreeKnowledge.ltreeKnowledge;
    },
    async editNode(id: number, name: string, description: string) {
      description = description ? description : null;
      const results = await mutation(
        `mutation editNode($id: Int!, $name: String!, $description: String){
          updateLtreeKnowledgeById(
            input: {
              id: $id,
              ltreeKnowledgePatch: {
                name: $name,
                description: $description
              }
            }
          ) {
            ltreeKnowledge {
              ${NODE_PROPS}
            }
          }
        }`,
        {
          id,
          name,
          description,
        },
      );
      return results.data.updateLtreeKnowledgeById.ltreeKnowledge;
    },
    async changeParent(id: number, newParentId: number) {
      const results = await mutation(
        `mutation editNode($id: Int!, $newParentId: Int!){
          updateLtreeKnowledgeById(
            input: {
              id: $id,
              ltreeKnowledgePatch: {
                parentId: $newParentId
              }
            }
          ) {
            ltreeKnowledge {
              ${NODE_PROPS}
            }
          }
        }`,
        {
          id,
          newParentId,
        },
      );
      return results.data.updateLtreeKnowledgeById.ltreeKnowledge;
    },
    async getRootNodes() {
      // Nodes representing categories are the true roots, but for UI purposes
      // the root of the graph is actually a node representing a discipline which
      // is a child of the selected category.
      const results = await query(
        `query getAllLtreeKnowledges2DexAdmin {
          allLtreeKnowledges(
            filter: {
              parentId: {
                isNull: true
              }
            }
          ){
            nodes{
              ${NODE_PROPS}
            }
          }
        }`,
      );
      return results.data.allLtreeKnowledges.nodes;
    },
  },
};
